// ============================================================
// accounts_toolbar_controller.js — /accounts 페이지 정렬·보기 toolbar UX
// ============================================================
// 역할:
//   1) view 토글 버튼(grid/group) 클릭 시 즉시 active 스타일 적용 (서버 응답 대기 X)
//   2) sort select 변경 시 spinner 표시
//   3) turbo:frame-render(#slot-list) 이벤트 수신 시 spinner 자동 숨김
//
// 사용 위치: app/views/accounts/_filter_tabs.html.erb 의 toolbar 부근
//
// 타깃 (data-accounts-toolbar-target):
//   - gridBtn  : 그리드 보기 버튼
//   - groupBtn : 앱 그룹 보기 버튼
//   - spinner  : 로딩 스피너 div
//
// 액션:
//   - click->accounts-toolbar#switchView    : view 토글 (gridBtn / groupBtn)
//   - change->accounts-toolbar#changeSort   : sort select onchange
//
// 데이터:
//   각 view 버튼은 data-view-mode="grid" 또는 "group" 가짐 (Stimulus 가 그 값으로 분기).
// ============================================================

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["gridBtn", "groupBtn", "spinner"]

  // 컨트롤러 connect 시 Turbo Frame 렌더 완료 이벤트 구독
  connect() {
    this._onFrameRender = (e) => {
      // #slot-list 프레임의 렌더 완료만 처리 (다른 프레임 이벤트는 무시)
      if (e.target && e.target.id === "slot-list") {
        this._hideSpinner()
      }
    }
    document.addEventListener("turbo:frame-render", this._onFrameRender)

    // 추가 안전망 — Turbo navigation 직후에도 spinner 가 남지 않도록 turbo:load 에서 숨김
    this._onTurboLoad = () => this._hideSpinner()
    document.addEventListener("turbo:load", this._onTurboLoad)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-render", this._onFrameRender)
    document.removeEventListener("turbo:load", this._onTurboLoad)
  }

  // ─────────────────────────────────────────────────────────
  // view 토글 버튼 클릭 — 즉시 active 토글 + spinner 표시
  // 기본 link 동작은 그대로 (Turbo 가 navigation 처리)
  // ─────────────────────────────────────────────────────────
  switchView(event) {
    const btn = event.currentTarget
    const newView = btn.dataset.viewMode  // "grid" 또는 "group"
    if (!newView) return

    this._setActiveView(newView)
    this._showSpinner()
    // event.preventDefault() 안 함 — 그래야 link_to 가 정상적으로 GET 요청 보냄
  }

  // ─────────────────────────────────────────────────────────
  // sort select 변경 — 즉시 spinner 표시
  // form 의 onchange="this.form.requestSubmit()" 이 별도로 동작하여 form 제출.
  // 본 핸들러는 spinner 토글만 담당.
  // ─────────────────────────────────────────────────────────
  changeSort() {
    this._showSpinner()
  }

  // ─────────────────────────────────────────────────────────
  // 내부 헬퍼: view 버튼 active 상태 토글
  // CSS 클래스 ft-view-btn--active 로 활성 표현. inline style 은 건드리지 않음.
  // ─────────────────────────────────────────────────────────
  _setActiveView(view) {
    if (!this.hasGridBtnTarget || !this.hasGroupBtnTarget) return
    const activeBtn   = view === "group" ? this.groupBtnTarget : this.gridBtnTarget
    const inactiveBtn = view === "group" ? this.gridBtnTarget  : this.groupBtnTarget
    activeBtn.classList.add("ft-view-btn--active")
    inactiveBtn.classList.remove("ft-view-btn--active")
    // aria-current 동기화 — 접근성
    activeBtn.setAttribute("aria-current", "page")
    inactiveBtn.removeAttribute("aria-current")
  }

  _showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.style.display = "inline-flex"
    }
  }

  _hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.style.display = "none"
    }
  }
}
